import type { BulkResult } from "@/domain/types/bulk";
import type {
  ClassificationDocumentTypeValue,
  ClassificationSortField,
} from "@/domain/classification/types";
import type { IClassificationRepository } from "@/application/ports/repositories";
import type {
  ClassificationResponse,
  ClassificationListResponse,
  ClassificationListParams,
  ClassificationExportParams,
  ClassificationAnalyticsParams,
  ClassificationLockInput,
  ClassificationSummaryParams,
  ReviewFlagResolutionInputValue,
  ClassificationTypeCountsParams,
  ClassificationUpdateRequest,
  ClassificationResolveDuplicateRequest,
  ClassificationResolveDuplicateResponse,
  ClassificationSelection,
  PipelineStatusBreakdown,
  SuppliersAnalyticsResponse,
  TaxBreakdownAnalyticsResponse,
  WorkforceAnalyticsResponse,
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  SignUploadBatchRequest,
  SignUploadBatchResponse,
  UploadSessionStatusResponse,
  ReviewClassificationRequest,
  ClassificationFileResponse,
  ClassificationLocksResponse,
  FieldLockInput,
} from "@/domain/classification";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob, downloadFromSignedUrl } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";
import type { AuditSummaryResponse } from "@/domain/audit";

function asArray<V>(value: V | V[] | undefined): V[] | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value : [value];
}

/** The summary takes one `status` and repeated `document_type`; only the type
 *  is widened here. The view carries the type as a plain string (a filter pill
 *  can hold a comma-separated group), so the cast to the SDK's enum is the
 *  frontier's job. */
function normalizeSummaryDocumentType<T extends { document_type?: string | string[] }>(
  params?: T,
): (Omit<T, "document_type"> & { document_type?: ClassificationDocumentTypeValue[] }) | undefined {
  if (!params) return undefined;
  const { document_type, ...rest } = params;
  return {
    ...rest,
    document_type: asArray(document_type) as ClassificationDocumentTypeValue[] | undefined,
  };
}

/** The list and the type counts take `document_type` and `status` as repeated
 *  query params; the callers pass one value or several, so both are widened. */
function normalizeRepeatable<T extends { document_type?: D | D[]; status?: S | S[] }, D, S>(
  params?: T,
): (Omit<T, "document_type" | "status"> & { document_type?: D[]; status?: S[] }) | undefined {
  if (!params) return undefined;
  const { document_type, status, ...rest } = params;
  return { ...rest, document_type: asArray(document_type), status: asArray(status) };
}

export class ClassificationRepository implements IClassificationRepository {
  async findAll(
    beneficiaryUuid: string,
    params?: ClassificationListParams,
    signal?: AbortSignal,
  ): Promise<ClassificationListResponse> {
    const { items, ...rest } = await unwrap(
      sdk.listClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: normalizeRepeatable(params),
        signal,
      }),
    );
    return { classifications: items, ...rest };
  }

  async getTypeCounts(
    beneficiaryUuid: string,
    params?: ClassificationTypeCountsParams,
    signal?: AbortSignal,
  ): Promise<Record<string, number>> {
    return unwrap(
      sdk.getClassificationStats({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: normalizeRepeatable(params),
        signal,
      }),
    );
  }

  async findById(
    beneficiaryUuid: string,
    classificationUuid: string,
    signal?: AbortSignal,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.getClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        signal,
      }),
    );
  }

  async getClassificationFile(
    beneficiaryUuid: string,
    classificationUuid: string,
    signal?: AbortSignal,
  ): Promise<ClassificationFileResponse> {
    return unwrap(
      sdk.getClassificationFile({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        signal,
      }),
    );
  }

  async getSummary(
    beneficiaryUuid: string,
    params?: ClassificationSummaryParams,
    signal?: AbortSignal,
  ): Promise<AuditSummaryResponse> {
    return unwrap(
      sdk.getClassificationSummary({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: normalizeSummaryDocumentType(params),
        signal,
      }),
    );
  }

  async getPipelineStatus(
    beneficiaryUuid: string,
    signal?: AbortSignal,
  ): Promise<PipelineStatusBreakdown> {
    return unwrap(
      sdk.getClassificationPipelineStatus({ path: { beneficiary_uuid: beneficiaryUuid }, signal }),
    );
  }

  async getSuppliersAnalytics(
    beneficiaryUuid: string,
    params?: ClassificationAnalyticsParams,
    signal?: AbortSignal,
  ): Promise<SuppliersAnalyticsResponse> {
    return unwrap(
      sdk.getSuppliersAnalytics({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
        signal,
      }),
    );
  }

  async getTaxBreakdown(
    beneficiaryUuid: string,
    params?: ClassificationAnalyticsParams,
    signal?: AbortSignal,
  ): Promise<TaxBreakdownAnalyticsResponse> {
    return unwrap(
      sdk.getTaxBreakdown({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
        signal,
      }),
    );
  }

  async getWorkforceAnalytics(
    beneficiaryUuid: string,
    params?: ClassificationAnalyticsParams,
    signal?: AbortSignal,
  ): Promise<WorkforceAnalyticsResponse> {
    return unwrap(
      sdk.getWorkforceAnalytics({
        path: { beneficiary_uuid: beneficiaryUuid },
        query: params,
        signal,
      }),
    );
  }

  async createUploadSession(
    beneficiaryUuid: string,
    data: CreateUploadSessionRequest,
  ): Promise<CreateUploadSessionResponse> {
    return unwrap(
      sdk.createUploadSession({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: data,
      }),
    );
  }

  async signUploadBatch(
    beneficiaryUuid: string,
    sessionUuid: string,
    data: SignUploadBatchRequest,
  ): Promise<SignUploadBatchResponse> {
    return unwrap(
      sdk.signUploadBatch({
        path: { beneficiary_uuid: beneficiaryUuid, session_uuid: sessionUuid },
        body: data,
      }),
    );
  }

  async getUploadSessionStatus(
    beneficiaryUuid: string,
    sessionUuid: string,
  ): Promise<UploadSessionStatusResponse> {
    return unwrap(
      sdk.getUploadSessionStatus({
        path: { beneficiary_uuid: beneficiaryUuid, session_uuid: sessionUuid },
      }),
    );
  }

  async updateClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: ClassificationUpdateRequest,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.updateClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: data,
      }),
    );
  }

  async reviewClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    data: ReviewClassificationRequest,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.reviewClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: data,
      }),
    );
  }

  async resolveDuplicate(
    beneficiaryUuid: string,
    data: ClassificationResolveDuplicateRequest,
  ): Promise<ClassificationResolveDuplicateResponse> {
    return unwrap(
      sdk.resolveDuplicate({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: data,
      }),
    );
  }

  async deleteClassification(beneficiaryUuid: string, classificationUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async bulkDeleteClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<BulkResult> {
    return unwrap(
      sdk.bulkDeleteClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: { classification_uuids: classificationUuids },
      }),
    );
  }

  async bulkDeleteMatchingClassifications(
    beneficiaryUuid: string,
    selection: ClassificationSelection,
  ): Promise<BulkResult> {
    return unwrap(
      sdk.bulkDeleteMatchingClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: selection,
      }),
    );
  }

  async bulkReclassifyMatchingClassifications(
    beneficiaryUuid: string,
    selection: ClassificationSelection,
  ): Promise<BulkResult> {
    return unwrap(
      sdk.bulkReclassifyMatchingClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: selection,
      }),
    );
  }

  async reclassifyClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    locks?: FieldLockInput[],
  ): Promise<ClassificationResponse> {
    // Body only when locks are provided: omit to preserve existing sticky
    // locks, send `{ locks }` to replace the set (empty array clears it).
    return unwrap(
      sdk.reclassifyClassification({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: locks === undefined ? undefined : { locks },
      }),
    );
  }

  async getClassificationLocks(
    beneficiaryUuid: string,
    classificationUuid: string,
  ): Promise<ClassificationLocksResponse> {
    return unwrap(
      sdk.getClassificationLocks({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
      }),
    );
  }

  async replaceClassificationLocks(
    beneficiaryUuid: string,
    classificationUuid: string,
    locks: ClassificationLockInput[],
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.replaceClassificationLocks({
        path: { beneficiary_uuid: beneficiaryUuid, classification_uuid: classificationUuid },
        body: { locks },
      }),
    );
  }

  async resolveReviewFlag(
    beneficiaryUuid: string,
    classificationUuid: string,
    flagUuid: string,
    resolution: ReviewFlagResolutionInputValue,
  ): Promise<ClassificationResponse> {
    return unwrap(
      sdk.resolveReviewFlag({
        path: {
          beneficiary_uuid: beneficiaryUuid,
          classification_uuid: classificationUuid,
          flag_uuid: flagUuid,
        },
        body: { resolution },
      }),
    );
  }

  async bulkReclassifyClassifications(
    beneficiaryUuid: string,
    classificationUuids: string[],
  ): Promise<BulkResult> {
    return unwrap(
      sdk.bulkReclassifyClassifications({
        path: { beneficiary_uuid: beneficiaryUuid },
        body: { classification_uuids: classificationUuids },
      }),
    );
  }

  async downloadClassification(
    beneficiaryUuid: string,
    classificationUuid: string,
    filename: string,
  ): Promise<void> {
    const fileResponse = await this.getClassificationFile(beneficiaryUuid, classificationUuid);
    await downloadFromSignedUrl(fileResponse.signed_url, filename);
  }

  async previewExport(
    params: ClassificationExportParams,
    signal?: AbortSignal,
  ): Promise<ExportPreview> {
    const { beneficiary_uuid, ...query } = params;
    return unwrap(
      sdk.previewClassificationsExport({
        path: { beneficiary_uuid },
        query: { ...query, status: asArray(query.status) },
        signal,
      }),
    );
  }

  async downloadExport(
    params: ClassificationExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { beneficiary_uuid, ...filters } = params;
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportClassifications,
      {
        path: { beneficiary_uuid },
        query: {
          ...filters,
          status: asArray(filters.status),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort_by: (options.sort_by as ClassificationSortField | undefined) ?? filters.sort_by,
          sort_direction: options.sort_direction ?? filters.sort_direction,
        },
      },
      `classifications.${options.format}`,
    );
    /**
     * Preset flows (e.g. "Solicitar correcciones al promotor") pass a
     * custom filename so the operator gets a self-describing file name
     * instead of the generic ``classifications_<timestamp>.xlsx`` the
     * backend produces.
     */
    downloadBlob(blob, options.filenameOverride ?? filename);
  }
}
